import { apiClient } from "./client";
import type { components } from "./schema";

/**
 * Typed client for `/api/v1/notifications/`, the in-app notifications the
 * alert, Desk and AI services write for one person.
 *
 * The rows are the signed-in user's own and nothing else: the server filters
 * on the user and never reads the `X-Context-*` scope headers, so these keys
 * carry the user and deliberately not `useScopeKey()`. Keying them by scope
 * would refetch the same answer on every scope switch and nothing more.
 */

export type AppNotification = components["schemas"]["Notification"];
type NotificationPage = components["schemas"]["PaginatedNotificationList"];

/** The newest rows the bell lists. The count covers every row, not just these. */
export const NOTIFICATION_LIST_SIZE = 20;

export interface NotificationList {
  results: AppNotification[];
  /** Every notification the user has, read or not, as the server counted them. */
  total: number;
}

export const notificationKeys = {
  all: ["notifications"] as const,
  // Keyed by user so a cached answer can never be served to the next person
  // to sign in on the tab; `authStore` also clears the whole cache on every
  // change of identity.
  forUser: (userId: string | null) =>
    [...notificationKeys.all, userId ?? "anon"] as const,
  unreadCount: (userId: string | null) =>
    [...notificationKeys.forUser(userId), "unread-count"] as const,
  list: (userId: string | null) =>
    [...notificationKeys.forUser(userId), "list"] as const,
};

export const notificationsApi = {
  /** How many are unread, counted by the server across every page. */
  unreadCount: async (): Promise<number> => {
    const res = await apiClient.get<
      components["schemas"]["NotificationUnreadCount"]
    >("/notifications/unread-count/");
    return res.data.count;
  },

  /** The newest notifications, newest first. */
  list: async (): Promise<NotificationList> => {
    const res = await apiClient.get<NotificationPage>("/notifications/", {
      params: { page_size: NOTIFICATION_LIST_SIZE },
    });
    const results = res.data.results ?? [];
    return { results, total: res.data.count ?? results.length };
  },

  markRead: async (id: number): Promise<AppNotification> => {
    const res = await apiClient.patch<AppNotification>(
      `/notifications/${id}/`,
      { is_read: true },
    );
    return res.data;
  },

  markAllRead: async (): Promise<void> => {
    await apiClient.post("/notifications/mark-all-read/");
  },
};

/**
 * The in-app route a notification leads to, or `null` when it has none.
 *
 * The services that raise notifications store app paths (`/alerts/7`,
 * `/desk/tickets/…`). Only those are followed: anything else, a full URL or a
 * `javascript:` string, is not drawn as a link at all, so a row can never send
 * the reader somewhere outside the app. `//host` and `/\host` are refused too,
 * because a browser reads both as another host.
 */
export function notificationTarget(n: AppNotification): string | null {
  const url = (n.action_url ?? "").trim();
  return /^\/(?![/\\])/.test(url) ? url : null;
}
